import type { Scene } from '@babylonjs/core/scene';
import type { Feature } from '@kb3d/models/classes/features/feature';
import { eMeshChangeTypes } from '@kb3d/models/classes/features/feature';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';
import type { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';

export type ProcessorData<T> = {
    data?: T;
    meshDynamicId: string;
};

/**
 * When adding a new processor, do the following:
 * 1. Create a new class that extends this class
 * 2. Add the worker payload and worker dependency data types ()
 * 3. Create a feature renderer that uses the new processor
 * 4. If the processor requires additional data:
 *   a. Implement the readWorkerPayload function to deserialize the worker payload
 *   b. Implement the updateMeshGeometry processor function to consume the Dependency Data
 *   c. (While in testing phase) Implement the updateMeshGeometry renderer function that generates the dependency data for comparing with the worker output
 * 5. Implement makeWorkerPayload in the feature renderer that returns the worker payload
 */

export abstract class FeatureGeometryProcessor<T extends Feature, DependencyData = any> {
    vertexDataCache = new Map<string, Partial<SubmeshVertexData>>();

    constructor(protected scene: Scene) {}

    readWorkerPayload(_workerHost: WorkerProcessorHost, _data: any | null | undefined): DependencyData | null {
        return undefined as DependencyData;
    }

    updateMeshGeometryCache(
        feature: T,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ): Partial<SubmeshVertexData> {
        const self = this;
        const newVertexData: Partial<SubmeshVertexData> = {};

        // Grab the properties in the mesh vertex that are needed for the feature
        // Only swap out the properties in the old vertex data with the stored copy if they have NOT been affected by another vertex
        if (feature.affectedBy) {
            const cachedData = this.vertexDataCache.get(feature._dynamicId);
            feature.affectedBy.forEach(affectedBy => {
                if (affectedBy === eMeshChangeTypes.VertexIndices) {
                    if (vertexDataChanges.indices === null) {
                        newVertexData.indices = null;
                        clearVertexDataCache('indices');
                    } else if (vertexDataChanges.indices) {
                        newVertexData.indices = vertexDataChanges.indices.slice();
                        updateVertexDataCache('indices', vertexDataChanges.indices);
                    } else if (cachedData) {
                        newVertexData.indices = cachedData.indices?.slice() as any;
                    } else {
                        if (vertexData.indices) {
                            newVertexData.indices = vertexData.indices.slice();
                            updateVertexDataCache('indices', vertexData.indices);
                        } else {
                            newVertexData.indices = null;
                        }
                    }

                    if (vertexDataChanges.paths === null) {
                        newVertexData.paths = null;
                        clearVertexDataCache('paths');
                    } else if (vertexDataChanges.paths) {
                        newVertexData.paths = vertexDataChanges.paths.slice();
                        updateVertexDataCache('paths', vertexDataChanges.paths);
                    } else if (cachedData) {
                        newVertexData.paths = cachedData.paths?.slice() as any;
                    } else {
                        if (vertexData.paths) {
                            newVertexData.paths = vertexData.paths.slice();
                            updateVertexDataCache('paths', vertexData.paths);
                        } else {
                            newVertexData.paths = null;
                        }
                    }

                    if (vertexDataChanges.holes === null) {
                        newVertexData.holes = null;
                        clearVertexDataCache('holes');
                    } else if (vertexDataChanges.holes) {
                        newVertexData.holes = vertexDataChanges.holes.slice();
                        updateVertexDataCache('holes', vertexDataChanges.holes);
                    } else if (cachedData) {
                        newVertexData.holes = cachedData.holes?.slice() as any;
                    } else {
                        if (vertexData.holes) {
                            newVertexData.holes = vertexData.holes.slice();
                            updateVertexDataCache('holes', vertexData.holes);
                        } else {
                            newVertexData.holes = null;
                        }
                    }
                }
                if (affectedBy === eMeshChangeTypes.VertexPositions) {
                    if (vertexDataChanges.positions === null) {
                        newVertexData.positions = null;
                        clearVertexDataCache('positions');
                    } else if (vertexDataChanges.positions) {
                        newVertexData.positions = vertexDataChanges.positions.slice();
                        updateVertexDataCache('positions', vertexDataChanges.positions);
                    } else if (cachedData) {
                        newVertexData.positions = cachedData.positions?.slice() as any; // compiler complains about the slice generic type for some reason
                    } else {
                        if (!vertexData.positions) {
                            throw Error('Invalid geometry: positions is null');
                        }
                        newVertexData.positions = vertexData.positions.slice();
                        updateVertexDataCache('positions', vertexData.positions);
                    }
                }
                if (affectedBy === eMeshChangeTypes.VertexNormals) {
                    if (vertexDataChanges.normals === null) {
                        newVertexData.normals = null;
                        clearVertexDataCache('normals');
                    } else if (vertexDataChanges.normals) {
                        newVertexData.normals = vertexDataChanges.normals.slice();
                        updateVertexDataCache('normals', vertexDataChanges.normals);
                    } else if (cachedData) {
                        newVertexData.normals = cachedData.normals?.slice() as any;
                    } else {
                        if (vertexData.normals) {
                            newVertexData.normals = vertexData.normals.slice();
                            updateVertexDataCache('normals', vertexData.normals);
                        } else {
                            newVertexData.normals = null;
                        }
                    }
                }
                if (affectedBy === eMeshChangeTypes.VertexUvs) {
                    if (vertexDataChanges.uvs === null) {
                        newVertexData.uvs = null;
                        clearVertexDataCache('uvs');
                    } else if (vertexDataChanges.uvs) {
                        newVertexData.uvs = vertexDataChanges.uvs.slice();
                        updateVertexDataCache('uvs', vertexDataChanges.uvs);
                    } else if (cachedData) {
                        newVertexData.uvs = cachedData.uvs?.slice() as any;
                    } else {
                        if (vertexData.uvs) {
                            newVertexData.uvs = vertexData.uvs.slice();
                            updateVertexDataCache('uvs', vertexData.uvs);
                        } else {
                            newVertexData.uvs = null;
                        }
                    }
                }
                if (affectedBy === eMeshChangeTypes.Submeshes) {
                    if (vertexDataChanges.submeshData === null) {
                        newVertexData.submeshData = null;
                        clearVertexDataCache('submeshData');
                    } else if (vertexDataChanges.submeshData) {
                        newVertexData.submeshData = JSON.parse(JSON.stringify(vertexDataChanges.submeshData));
                        updateVertexDataCache('submeshData', JSON.parse(JSON.stringify(vertexDataChanges.submeshData)));
                    } else if (cachedData) {
                        const cachedSubmeshes = cachedData.submeshData;
                        newVertexData.submeshData = cachedSubmeshes
                            ? JSON.parse(JSON.stringify(cachedSubmeshes))
                            : undefined;
                    } else {
                        if (vertexData.submeshData) {
                            newVertexData.submeshData = JSON.parse(JSON.stringify(vertexData.submeshData));
                            updateVertexDataCache('submeshData', JSON.parse(JSON.stringify(vertexData.submeshData)));
                        } else {
                            newVertexData.submeshData = null;
                        }
                    }
                }
                if (affectedBy === eMeshChangeTypes.Colors) {
                    if (vertexDataChanges.colors === null) {
                        newVertexData.colors = null;
                        clearVertexDataCache('colors');
                    } else if (vertexDataChanges.colors) {
                        newVertexData.colors = vertexDataChanges.colors.slice();
                        updateVertexDataCache('colors', vertexDataChanges.colors);
                    } else if (cachedData) {
                        newVertexData.colors = cachedData.colors?.slice() as any;
                    } else {
                        if (vertexData.colors) {
                            newVertexData.colors = vertexData.colors.slice();
                            updateVertexDataCache('colors', vertexData.colors);
                        } else {
                            newVertexData.colors = null;
                        }
                    }
                }
            });
        }

        function clearVertexDataCache(prop: keyof SubmeshVertexData) {
            const vertexDataCache = self.vertexDataCache.get(feature._dynamicId);
            if (vertexDataCache) {
                delete vertexDataCache[prop];
            }
        }

        function updateVertexDataCache(prop: keyof SubmeshVertexData, value: any) {
            if (!value) {
                throw Error('invalid geometry does not have ' + prop);
            }

            let vertexDataCache = self.vertexDataCache.get(feature._dynamicId);
            if (!vertexDataCache) {
                vertexDataCache = {};
                self.vertexDataCache.set(feature._dynamicId, vertexDataCache);
            }

            if (typeof value.slice !== 'function') {
                throw Error('fixme: how do I clone this property?');
            }
            vertexDataCache[prop] = value.slice(); // Need to make sure everything is cloned when inserted into the cache
        }

        return newVertexData;
    }

    updateMeshGeometry(
        feature: T,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        _dependencyData: ProcessorData<DependencyData>
    ): Partial<SubmeshVertexData> {
        return this.updateMeshGeometryCache(feature, vertexData, vertexDataChanges);
    }

    clearCache(features?: Feature[]) {
        if (features) {
            features.forEach(feature => {
                this.vertexDataCache.delete(feature._dynamicId);
            });
        } else {
            this.vertexDataCache.clear();
        }
    }

    delete3d(feature: T, preserveCache?: boolean) {
        if (!preserveCache) {
            this.vertexDataCache.delete(feature._dynamicId);
        }
    }

    dispose() {
        this.clearCache();
    }
}
